import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, signInWithEmailAndPassword, signOut } from '@angular/fire/auth';
import { Actions, createEffect, ofType, CreateEffectMetadata } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { SciGuaduaHttpRequestHandler, TransactionHelper } from '@scifamek/guadua';
import { EMPTY, Observable, from, of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';

import AppState from '../../../app.state';
import { AppRoutes } from '../../../core/constants/routes.constants';
import { Login } from '../../../core/views/login/login';
import { environment } from '../../../../environments/environment';
import {
  LoginCommand,
  LogoutCommand,
  GetUsersByAuthIdCommand,
  GetAuthorizationTokenCommand,
  GetResourcesCommand,
  GetPermissionsByResourceCommand,
  GetResourcesByQueryCommand,
  GetPermissionsByQueryCommand,
  GetPermissionsCommand,
  GetRolesByPermissionCommand,
  GetRolesCommand,
  GetRolesByQueryCommand,
} from './auth.commands';
import {
  AuthenticationTokenGottenEvent,
  AuthorizationTokenGottenEvent,
  LoginFailedEvent,
  LogoutEvent,
  ResourcesGottenEvent,
  UsersGottenEvent,
  UserKindSelectedEvent,
  SessionRehydratedEvent,
  GottenResourcesDomainEvent,
  GottenPermissionByResourceDomainEvent,
  GottenResourcesByQueryDomainEvent,
  GottenPermissionsDomainEvent,
  GottenRolesByPermissionDomainEvent,
  GottenPermissionsByQueryDomainEvent,
  GottenRolesDomainEvent,
  GottenRolesByQueryDomainEvent,
} from './auth.events';
import { GetRolesByPermissionParam } from '@resident-select/domain/role';

interface AuthorizationTokenPayload {
  resources: string[];
  [key: string]: any;
}

@Injectable()
export class AuthEffects {
  resourcePrefix = 'resource';
  rolePrefix = 'role';
  permissionPrefix = 'permission';

  private authId: string = '';
  private lastSelectedUserKind: string = '';

  loginEffect!: Observable<Action<string>> & CreateEffectMetadata;
  getUsersEffect!: Observable<Action<string>> & CreateEffectMetadata;
  fetchUsersAfterAuthEffect!: Observable<Action<string>> & CreateEffectMetadata;
  fetchUsersAfterRehydrationEffect!: Observable<Action<string>> & CreateEffectMetadata;
  autoSelectUserEffect!: Observable<Action<string>> & CreateEffectMetadata;
  getAuthorizationTokenEffect!: Observable<Action<string>> & CreateEffectMetadata;
  fetchAuthorizationTokenEffect!: Observable<Action<string>> & CreateEffectMetadata;
  decodeTokenEffect!: Observable<Action<string>> & CreateEffectMetadata;
  navigateEffect!:
    | (Observable<Action<string>> & CreateEffectMetadata)
    | (Observable<void> & CreateEffectMetadata);
  logoutEffect!: Observable<Action<string>> & CreateEffectMetadata;

  getResourcesEffect!: Observable<Action<string>> & CreateEffectMetadata;
  getPermissionsByResourceEffect: Observable<Action<string>> & CreateEffectMetadata;
  getResourcesByQueryEffect: Observable<Action<string>> & CreateEffectMetadata;

  getPermissionsEffect!: Observable<Action<string>> & CreateEffectMetadata;
  getRolesByPermissionEffect: Observable<Action<string>> & CreateEffectMetadata;
  getPermissionsByQueryEffect: Observable<Action<string>> & CreateEffectMetadata;

  getRolesEffect!: Observable<Action<string>> & CreateEffectMetadata;
  getRolesByQueryEffect: Observable<Action<string>> & CreateEffectMetadata;

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<AppState>,
    private readonly router: Router,
    private readonly auth: Auth,
    private readonly transactionHelper: TransactionHelper,
    private readonly dialog: MatDialog,
    private readonly http: HttpClient,
    public sciGuaduaHttpRequestHandler: SciGuaduaHttpRequestHandler,
  ) {
    this.loginEffect = createEffect(() =>
      this.actions$.pipe(
        ofType(LoginCommand),
        switchMap(({ username, password }) =>
          from(signInWithEmailAndPassword(this.auth, username, password)).pipe(
            switchMap((userCredential) =>
              from(userCredential.user.getIdToken()).pipe(
                map((idToken) => {
                  this.authId = userCredential.user.uid;
                  return AuthenticationTokenGottenEvent({ authenticationToken: idToken });
                }),
              ),
            ),
            catchError(() => of(LoginFailedEvent())),
          ),
        ),
      ),
    );

    this.getUsersEffect = this.sciGuaduaHttpRequestHandler.handleEvent({
      command: GetUsersByAuthIdCommand,
      prefix: 'authentication',
      others: (httpResponse: any, _commandData: any) => {
        const data = httpResponse.data;
        const users = Array.isArray(data) ? data : data ? [data] : [];
        return [UsersGottenEvent({ users })];
      },
    });

    //--- Roles, permisos y recursos
    this.getResourcesEffect = this.sciGuaduaHttpRequestHandler.handleEvent({
      command: GetResourcesCommand,
      prefix: this.resourcePrefix,
      others: (httpResponse: any, _commandData: any) => {
        const data = httpResponse.data;

        return [
          GottenResourcesDomainEvent({
            resources: data,
          }),
        ];
      },
    });
    this.getPermissionsByResourceEffect = this.sciGuaduaHttpRequestHandler.handleEvent({
      command: GetPermissionsByResourceCommand,
      prefix: this.permissionPrefix,
      others: (httpResponse: any, _commandData: any) => {
        const data = httpResponse.data;

        return [
          GottenPermissionByResourceDomainEvent({
            permissions: data,
            resourceId: _commandData.resourceId,
          }),
        ];
      },
    });
    this.getResourcesByQueryEffect = this.sciGuaduaHttpRequestHandler.handleEvent({
      command: GetResourcesByQueryCommand,
      prefix: this.resourcePrefix,
      others: (httpResponse: any, _commandData: any) => {
        const data = httpResponse.data;

        return [
          GottenResourcesByQueryDomainEvent({
            resources: data,
          }),
        ];
      },
    });

    this.getPermissionsEffect = this.sciGuaduaHttpRequestHandler.handleEvent({
      command: GetPermissionsCommand,
      prefix: this.permissionPrefix,
      others: (httpResponse: any, _commandData: any) => {
        const data = httpResponse.data;

        return [
          GottenPermissionsDomainEvent({
            permissions: data,
          }),
        ];
      },
    });
    this.getRolesByPermissionEffect = this.sciGuaduaHttpRequestHandler.handleEvent({
      command: GetRolesByPermissionCommand,
      prefix: this.rolePrefix,
      others: (httpResponse: any, _commandData: GetRolesByPermissionParam) => {
        const data = httpResponse.data;

        return [
          GottenRolesByPermissionDomainEvent({
            roles: data,
            permissionId: _commandData.permissionId,
          }),
        ];
      },
    });
    this.getPermissionsByQueryEffect = this.sciGuaduaHttpRequestHandler.handleEvent({
      command: GetPermissionsByQueryCommand,
      prefix: this.permissionPrefix,
      others: (httpResponse: any, _commandData: any) => {
        const data = httpResponse.data;
        return [
          GottenPermissionsByQueryDomainEvent({
            permissions: data,
          }),
        ];
      },
    });

    this.getRolesEffect = this.sciGuaduaHttpRequestHandler.handleEvent({
      command: GetRolesCommand,
      prefix: this.rolePrefix,
      others: (httpResponse: any, _commandData: any) => {
        const data = httpResponse.data;

        return [
          GottenRolesDomainEvent({
            roles: data,
          }),
        ];
      },
    });
    this.getRolesByQueryEffect = this.sciGuaduaHttpRequestHandler.handleEvent({
      command: GetRolesByQueryCommand,
      prefix: this.rolePrefix,
      others: (httpResponse: any, _commandData: any) => {
        const data = httpResponse.data;
        return [
          GottenRolesByQueryDomainEvent({
            roles: data,
          }),
        ];
      },
    });
    this.fetchUsersAfterAuthEffect = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthenticationTokenGottenEvent),
        map(() => {
          const transactionId = this.transactionHelper.identifier({ authId: this.authId });
          return GetUsersByAuthIdCommand({ authId: this.authId, transactionId });
        }),
      ),
    );

    this.fetchUsersAfterRehydrationEffect = createEffect(() =>
      this.actions$.pipe(
        ofType(SessionRehydratedEvent),
        map(({ authenticationToken }) => {
          try {
            const decoded = jwtDecode<{ sub?: string; user_id?: string }>(authenticationToken);
            const authId = decoded.sub || decoded.user_id || '';
            this.authId = authId;
            const transactionId = this.transactionHelper.identifier({ authId });
            return GetUsersByAuthIdCommand({ authId, transactionId });
          } catch {
            return null;
          }
        }),
        switchMap((action) => (action ? of(action) : EMPTY)),
      ),
    );

    this.autoSelectUserEffect = createEffect(() =>
      this.actions$.pipe(
        ofType(UsersGottenEvent),
        withLatestFrom(this.store.select((state: AppState) => state.auth.authorizationToken)),
        switchMap(([{ users }, authorizationToken]) => {
          // Skip auto-select if session already has an authorization token (rehydration)
          if (authorizationToken) {
            return EMPTY;
          }
          if (users.length === 1) {
            this.lastSelectedUserKind = users[0].userKind;
            return of(UserKindSelectedEvent({ userKind: users[0].userKind, authId: this.authId }));
          }
          return EMPTY;
        }),
      ),
    );

    this.getAuthorizationTokenEffect = this.sciGuaduaHttpRequestHandler.handleEvent({
      command: GetAuthorizationTokenCommand,
      prefix: 'authentication',
      others: (httpResponse: any, _commandData: any) => {
        const data = httpResponse.data;
        const token = typeof data === 'string' ? data : data?.token || '';
        return [AuthorizationTokenGottenEvent({ authorizationToken: token })];
      },
    });

    this.fetchAuthorizationTokenEffect = createEffect(() =>
      this.actions$.pipe(
        ofType(UserKindSelectedEvent),
        map(({ authId, userKind }) => {
          const transactionId = this.transactionHelper.identifier({ authId, userKind });
          return GetAuthorizationTokenCommand({ authId, userKind, transactionId });
        }),
      ),
    );

    this.decodeTokenEffect = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthorizationTokenGottenEvent),
        map(({ authorizationToken }) => {
          try {
            const payload = jwtDecode<AuthorizationTokenPayload>(authorizationToken);
            console.log({ payload });

            return ResourcesGottenEvent({ resources: payload.resources || [] });
          } catch {
            return LoginFailedEvent();
          }
        }),
      ),
    );

    this.navigateEffect = createEffect(
      () =>
        this.actions$.pipe(
          ofType(ResourcesGottenEvent),
          tap(() => {
            const userKind = this.lastSelectedUserKind;
            if (userKind === 'doorman') {
              this.router.navigate([`/${AppRoutes.DOORMAN_DASHBOARD}`]);
            } else {
              this.router.navigate([`/${AppRoutes.SUPER_ADMIN_DASHBOARD}`]);
            }
          }),
        ),
      { dispatch: false },
    );

    this.logoutEffect = createEffect(() =>
      this.actions$.pipe(
        ofType(LogoutCommand),
        withLatestFrom(this.store.select((state: AppState) => state.auth.authorizationToken)),
        switchMap(([_, authorizationToken]) => {
          const invalidate$ = authorizationToken
            ? this.http
                .post(`${environment.server}/authentication/logout`, { token: authorizationToken })
                .pipe(catchError(() => of(null)))
            : of(null);

          return invalidate$.pipe(
            switchMap(() => from(signOut(this.auth))),
            tap(() => {
              this.dialog.closeAll();
              this.router.navigate([Login.route]);
            }),
            map(() => LogoutEvent()),
            catchError(() => of(LogoutEvent())),
          );
        }),
      ),
    );
  }
}
